// src/pages/SalesPages/CashOutModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import baseStyles from "../../styles/SalesMode.module.css";
import s from "./CashOutModal.module.css";
import { API_URL } from "../../config/api";

const CashOutModal = ({ open, onClose, warehouse }) => {
  const [amountStr, setAmountStr] = useState("0.00");
  const [categoryId, setCategoryId] = useState("");
  const [comment, setComment] = useState("");
  const [categories, setCategories] = useState([]);
  const [balance, setBalance] = useState(null);

  const [walletId, setWalletId] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  const amountNumber = useMemo(() => {
    const v = (amountStr || "").replace(",", ".");
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, [amountStr]);

  // ===== keypad helpers =====
  const setSafeAmount = (next) => {
    let v = String(next ?? "");
    v = v.replace(",", ".");

    if (!/^\d*\.?\d*$/.test(v)) return;

    const [, b] = v.split("."); // ✅ fixed
    if (b && b.length > 2) return;

    if (v === "") v = "0.00";

    setAmountStr(v);
  };


  const handleKey = (k) => {
    if (k === "C") return setAmountStr("0.00");
    if (k === "⌫") {
      const s0 = amountStr;
      if (s0.length <= 1) return setAmountStr("0.00");
      const next = s0.slice(0, -1);
      return setAmountStr(next === "" ? "0.00" : next);
    }

    let next = amountStr;

    if ((next === "0.00" || next === "0") && k !== ".") next = "";
    if (k === "." && next.includes(".")) return;
    if (k === "." && next === "") next = "0";

    setSafeAmount(next + k);
  };

  // ===== init on open =====
  useEffect(() => {
    if (!open) return;
    setAmountStr("0.00");
    setCategoryId("");
    setComment("");
    setWalletId(null);
    setBalance(null);
  }, [open]);

  // ===== 1) fetch wallets(type=cash) -> find wallet for current warehouse -> set walletId + balance =====
  useEffect(() => {
    if (!open || !token || !warehouse?.id) return;

    const run = async () => {
      try {
        const res = await fetch(`${API_URL}/api/wallets?type=cash`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          console.error("wallets non-JSON:", text?.slice(0, 200));
          return;
        }

        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        if (!list.length) {
          alert("Не найдено кошельков типа cash");
          return;
        }

        const found = list.find(
          (w) => Number(w.storage_id) === Number(warehouse.id) && w.type === "cash"
        );

        if (!found?.id) {
          alert("Для этого склада не найден cash-кошелек (wallet)");
          return;
        }

        setWalletId(found.id);

        const b = found.balance ?? null;
        setBalance(b != null ? Number(b) : null);
      } catch (e) {
        console.error("CashOutModal wallets fetch error:", e);
        alert("Ошибка получения кошельков");
      }
    };

    run();
  }, [open, token, warehouse?.id]);

  // ===== categories (оставил как было) =====
  useEffect(() => {
    if (!open || !token) return;

    const run = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cash_categories?type=withdraw`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const text = await res.text();
        const data = JSON.parse(text);

        const list = Array.isArray(data?.data) ? data.data : [];
        if (list.length) {
          setCategories(
            list.map((c) => ({
              id: String(c.id),
              title: c.title || c.name || `Категория ${c.id}`,
            }))
          );
          return;
        }
      } catch (e) {}

      setCategories([
        { id: "1", title: "Инкассация" },
        { id: "2", title: "Выдача под отчет" },
        { id: "3", title: "Прочее" },
      ]);
    };

    run();
  }, [open, token]);

  // ===== 2) withdraw by walletId =====
  const handleSubmit = async () => {
    if (!token) return alert("Нет токена");
    if (!warehouse?.id) return alert("Нет склада");
    if (!walletId) return alert("Не найден wallet для этого склада");
    if (amountNumber <= 0) return alert("Укажите сумму больше 0");

    // если знаем баланс — проверим
    if (balance != null && amountNumber > Number(balance)) {
      alert("Нельзя изъять больше, чем баланс кассы");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/wallets/${walletId}/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: amountNumber,
          // если бек попросит — можно легко добавить:
          // category_id: categoryId ? Number(categoryId) : null,
          // comment: comment?.trim() || null,
        }),
      });

      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {}

      if (!res.ok) {
        alert(data?.message || `Ошибка (${res.status})`);
        return;
      }

      onClose?.();
    } catch (e) {
      console.error(e);
      alert("Ошибка при подключении к API");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={baseStyles.modalOverlay}>
      <div className={`${baseStyles.modal} ${s.modal}`}>
        {/* LEFT numpad */}
        <div className={s.left}>
          <div className={s.quickGrid}>
            {[1000, 2000, 5000, 10000].map((v) => (
              <button key={v} onClick={() => setSafeAmount(String(v))} className={s.quickBtn}>
                {v}
              </button>
            ))}
          </div>

          <div className={s.pad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, ".", 0].map((n) => (
              <button key={n} onClick={() => handleKey(String(n))} className={s.keyBtn}>
                {n}
              </button>
            ))}

            <button onClick={() => handleKey("⌫")} className={s.keyBtn}>
              ⌫
            </button>

            <button onClick={() => handleKey("C")} className={s.clearBtn}>
              C
            </button>
          </div>
        </div>

        {/* RIGHT form */}
        <div className={s.right}>
          <div className={s.top}>
            <div>
              <div className={s.subTitle}>Денежный баланс кассы “{warehouse?.name || "—"}”</div>
              <div className={s.balance}>
                {balance == null ? "—" : `${Number(balance).toLocaleString()} ₸`}
              </div>
              <div className={s.walletLine}>-----------------</div>
            </div>
          </div>

          <div className={s.form}>
            <div className={s.field}>
              <div className={s.label}>Сумма изъятия</div>
              <div className={s.amountInputWrap}>
                <input
                  value={amountStr}
                  onChange={(e) => setSafeAmount(e.target.value)}
                  className={s.amountInput}
                />
                <span className={s.currency}>₸</span>
              </div>
            </div>

            <div className={s.field}>
              <div className={s.label}>Категория изъятия</div>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={s.select}
              >
                <option value="">Категория изъятия</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            <div className={s.field}>
              <div className={s.label}>Комментарий</div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={255}
                className={s.textarea}
                placeholder="Комментарий"
              />
              <div className={s.counter}>Осталось символов: {255 - (comment?.length || 0)}</div>
            </div>

            <div className={s.actions}>
              <button type="button" onClick={onClose} className={s.cancelBtn}>
                Отменить
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !walletId}
                className={s.primaryBtn}
                title={!walletId ? "Не найден wallet для этого склада" : ""}
              >
                {loading ? "..." : "Изъять сумму"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashOutModal;
